import { COMPANY_TABS } from "../Views/Private/_Companies/_MODULES/_Navigation";
import { PROJECT_TABS, SUBPROJECT_TABS } from "../Views/Private/_Projects/_MODULES/_Navigation";
import { SAVINGS_TABS } from "../Views/Private/_Savings/_MODULES/_Navigation";
import { MONTHLY_EXPENSES_TABS } from "../Views/Private/_MonthlyExpenses/_MODULES/_Navigation";
import { COURSE_TABS } from "../Views/Private/_Courses/_MODULES/_Navigation";
import { TOOL_GROUPS } from "../Views/Private/_Tools/_MODULES/_Navigation";

const routeExists = (routes, path) => routes.some((route) => route.path === path);

const leaf = (id, path, label, options = {}) => ({
    id,
    path,
    label,
    kind: "leaf",
    ...options,
});

const section = (id, path, label, children = [], options = {}) => ({
    id,
    path,
    label,
    kind: "section",
    children,
    ...options,
});

const category = (id, label, children = []) => ({
    id,
    path: children[0]?.path ?? `/${id}`,
    label,
    kind: "category",
    children,
});

const parseContext = (pathname = "") => {
    const segments = String(pathname).split("/").filter(Boolean);

    return {
        companyId: segments[0] === "companies" ? segments[1] ?? null : null,
        projectId: segments[0] === "projects" ? segments[1] ?? null : null,
        subProjectId: segments[0] === "projects" && segments[2] === "subprojects" ? segments[3] ?? null : null,
        courseId: segments[0] === "courses" ? segments[1] ?? null : null,
    };
};

const buildCompanyChildren = (companyId) => [
    leaf("companies-list", "/companies", "Todas las compañías"),
    ...(companyId ? COMPANY_TABS.map((tab, index) => leaf(
        `company-${tab.value}`,
        `/companies/${companyId}/${tab.value}`,
        tab.label,
        index === 0
            ? { aliases: [`/companies/${companyId}`] }
            : (tab.value === "vault" ? { match: "prefix" } : {}),
    )) : []),
];

const buildProjectChildren = (projectId, subProjectId) => [
    leaf("projects-list", "/projects", "Todos los proyectos"),
    ...(projectId ? PROJECT_TABS.map((tab, index) => leaf(
        `project-${tab.value}`,
        `/projects/${projectId}/${tab.value}`,
        tab.label,
        index === 0 ? { aliases: [`/projects/${projectId}`] } : (
            tab.value === "guides" ? { match: "prefix" } : {}
        ),
    )) : []),
    ...(projectId && subProjectId ? [
        section(
            "current-subproject",
            `/projects/${projectId}/subprojects/${subProjectId}`,
            "Subproyecto actual",
            SUBPROJECT_TABS.map((tab, index) => leaf(
                `subproject-${tab.value}`,
                `/projects/${projectId}/subprojects/${subProjectId}/${tab.value}`,
                tab.label,
                index === 0
                    ? { aliases: [`/projects/${projectId}/subprojects/${subProjectId}`] }
                    : (tab.value === "guides" ? { match: "prefix" } : {}),
            )),
        ),
    ] : []),
];

const buildCourseChildren = (courseId) => [
    leaf("courses-list", "/courses", "Todos los cursos"),
    ...(courseId ? COURSE_TABS.map((tab, index) => leaf(
        `course-${tab.value}`,
        `/courses/${courseId}/${tab.value}`,
        tab.label,
        index === 0 ? { aliases: [`/courses/${courseId}`] } : {},
    )) : []),
];

const buildSavingsChildren = () => SAVINGS_TABS.map((tab, index) => leaf(
    `savings-${tab.value}`,
    `/savings/${tab.value}`,
    tab.label,
    index === 0 ? { aliases: ["/savings"] } : {},
));

const buildMonthlyExpensesChildren = () => MONTHLY_EXPENSES_TABS.map((tab, index) => leaf(
    `monthly-expenses-${tab.value}`,
    `/monthly-expenses/${tab.value}`,
    tab.label,
    index === 0 ? { aliases: ["/monthly-expenses"] } : {},
));

export const buildPrivateSidebarNavigation = ({ pathname = "", routes = [] } = {}) => {
    const context = parseContext(pathname);
    const items = [];

    // Acceso inmediato: vive arriba de las áreas, justo debajo del logo.
    if (routeExists(routes, "/sticky-notes")) {
        items.push(leaf("sticky-notes", "/sticky-notes", "Notas rápidas", {
            match: "prefix", icon: "sticky-note", emphasis: "utility", placement: "top",
        }));
    }

    const workChildren = [];
    if (routeExists(routes, "/dashboard")) workChildren.push(leaf("work-dashboard", "/dashboard", "Panel"));
    if (routeExists(routes, "/companies")) workChildren.push(section("companies", "/companies", "Compañías", buildCompanyChildren(context.companyId)));
    if (routeExists(routes, "/projects")) workChildren.push(section("projects", "/projects", "Proyectos", buildProjectChildren(context.projectId, context.subProjectId)));
    if (routeExists(routes, "/tags")) workChildren.push(leaf("tags", "/tags", "Etiquetas", { match: "prefix" }));
    if (workChildren.length) items.push(category("work", "Trabajo", workChildren));

    const securityChildren = [];
    if (routeExists(routes, "/security")) securityChildren.push(leaf("security-dashboard", "/security", "Panel"));
    if (routeExists(routes, "/personal-vault")) securityChildren.push(
        leaf("personal-vault", "/personal-vault", "Bóveda personal", { match: "prefix" }),
    );
    if (securityChildren.length) items.push(category("security", "Seguridad", securityChildren));

    const shelfChildren = [];
    if (routeExists(routes, "/shelf")) shelfChildren.push(leaf("shelf-dashboard", "/shelf", "Panel"));
    if (routeExists(routes, "/library")) shelfChildren.push(leaf("library", "/library", "Biblioteca", { match: "prefix" }));
    if (routeExists(routes, "/manuals")) shelfChildren.push(
        leaf("manuals", "/manuals", "Manuales", { match: "prefix" }),
    );
    if (routeExists(routes, "/courses")) shelfChildren.push(section("courses", "/courses", "Cursos", buildCourseChildren(context.courseId)));
    if (shelfChildren.length) items.push(category("shelf", "Estantería", shelfChildren));

    const financeChildren = [];
    if (routeExists(routes, "/finance")) financeChildren.push(leaf("finance-dashboard", "/finance", "Panel"));
    if (routeExists(routes, "/savings")) financeChildren.push(section("savings", "/savings", "Ahorrador", buildSavingsChildren()));
    if (routeExists(routes, "/monthly-expenses")) financeChildren.push(section("monthly-expenses", "/monthly-expenses", "Gastos mensuales", buildMonthlyExpensesChildren()));
    if (financeChildren.length) items.push(category("finance", "Finanzas", financeChildren));

    const toolsChildren = [];
    if (routeExists(routes, "/tools")) {
        toolsChildren.push(leaf("tools-dashboard", "/tools", "Panel"));
        TOOL_GROUPS.forEach((group) => {
            const children = group.items
                .filter((tool) => routeExists(routes, `/tools/${tool.value}`))
                .map((tool) => leaf(`tools-${tool.value}`, `/tools/${tool.value}`, tool.label));
            if (children.length) toolsChildren.push(section(`tools-${group.id}`, children[0].path, group.label, children));
        });
    }
    if (toolsChildren.length) items.push(category("tools", "Herramientas", toolsChildren));

    // Organización va debajo de Herramientas para mantener juntas primero las áreas de trabajo.
    const organizeChildren = [];
    if (routeExists(routes, "/today")) organizeChildren.push(leaf("today", "/today", "Hoy", { icon: "bolt" }));
    if (routeExists(routes, "/inbox")) organizeChildren.push(leaf("inbox", "/inbox", "Inbox", { icon: "inbox" }));
    if (routeExists(routes, "/reminders")) organizeChildren.push(leaf("reminders", "/reminders", "Recordatorios", { icon: "bell" }));
    if (routeExists(routes, "/favorites")) organizeChildren.push(leaf("favorites", "/favorites", "Favoritos", { icon: "star" }));
    if (routeExists(routes, "/activity")) organizeChildren.push(leaf("activity", "/activity", "Actividad", { icon: "history" }));
    if (routeExists(routes, "/relations")) organizeChildren.push(leaf("relations", "/relations", "Relaciones", { icon: "link" }));
    if (routeExists(routes, "/versions")) organizeChildren.push(leaf("versions", "/versions", "Versiones", { icon: "history" }));
    if (routeExists(routes, "/templates")) organizeChildren.push(leaf("templates", "/templates", "Plantillas", { icon: "th-large" }));
    if (routeExists(routes, "/runbooks")) organizeChildren.push(leaf("runbooks", "/runbooks", "Runbooks", { icon: "list" }));
    if (routeExists(routes, "/backup")) organizeChildren.push(leaf("backup", "/backup", "Backup", { icon: "database" }));
    if (organizeChildren.length) items.push(category("organize", "Organización", organizeChildren));

    // Utilidades globales: quedan al pie del sidebar, separadas de las areas de trabajo.
    if (routeExists(routes, "/commands")) {
        items.push(leaf("commands", "/commands", "Comandos", {
            match: "prefix", icon: "terminal", emphasis: "utility", placement: "bottom-start",
        }));
    }

    if (routeExists(routes, "/snippets")) {
        items.push(leaf("snippets", "/snippets", "Snippets", {
            match: "prefix", icon: "code", emphasis: "utility", placement: "bottom",
        }));
    }

    if (routeExists(routes, "/wishlist")) {
        items.push(leaf("wishlist", "/wishlist", "Wish List", {
            match: "prefix", icon: "heart", emphasis: "utility", placement: "bottom",
        }));
    }

    if (routeExists(routes, "/contacts")) {
        items.push(leaf("contacts", "/contacts", "Contactos", {
            match: "prefix", icon: "address-book", emphasis: "utility", placement: "bottom",
        }));
    }

    if (routeExists(routes, "/notebook")) {
        items.push(leaf("notebook", "/notebook", "Cuaderno", {
            match: "prefix", icon: "book", emphasis: "utility", placement: "bottom",
        }));
    }

    return items;
};
